import { useQuery } from '@tanstack/react-query';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import api from '../lib/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

interface AnalyticsBucket {
  _id: string;
  count: number;
}

export default function AnalyticsPage() {
  const { data: userAnalytics, isLoading: loadingUsers } = useQuery({
    queryKey: ['analytics', 'users'],
    queryFn: async () => {
      const res = await api.get('/admin/analytics/users');
      return res.data.data;
    },
  });

  const { data: contentAnalytics, isLoading: loadingContent } = useQuery({
    queryKey: ['analytics', 'content'],
    queryFn: async () => {
      const res = await api.get('/admin/analytics/content');
      return res.data.data;
    },
  });

  const isLoading = loadingUsers || loadingContent;

  const sortedUsers = [...(userAnalytics ?? [])].reverse();
  const userGrowthData = {
    labels: sortedUsers.map((m: AnalyticsBucket) => m._id) ?? [],
    datasets: [
      {
        label: 'New Users',
        data: sortedUsers.map((m: AnalyticsBucket) => m.count) ?? [],
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderRadius: 6,
      },
    ],
  };

  const categoryData = {
    labels: contentAnalytics?.byCategory?.map((c: AnalyticsBucket) => c._id) ?? [],
    datasets: [
      {
        label: 'Questions',
        data: contentAnalytics?.byCategory?.map((c: AnalyticsBucket) => c.count) ?? [],
        backgroundColor: 'rgba(139, 92, 246, 0.7)',
        borderRadius: 6,
      },
    ],
  };

  const typeData = {
    labels: contentAnalytics?.byType?.map((c: AnalyticsBucket) => c._id) ?? [],
    datasets: [
      {
        data: contentAnalytics?.byType?.map((c: AnalyticsBucket) => c.count) ?? [],
        backgroundColor: ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981'],
      },
    ],
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-500 mt-1">Platform usage and content analytics</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h2>
          <Bar
            data={userGrowthData}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Questions by Type</h2>
          <div className="max-w-xs mx-auto">
            <Doughnut data={typeData} options={{ responsive: true }} />
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Questions by Category</h2>
          <Bar
            data={categoryData}
            options={{ responsive: true, plugins: { legend: { display: false } } }}
          />
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Questions by Difficulty</h2>
          <div className="space-y-3">
            {(contentAnalytics?.byDifficulty ?? []).map((d: AnalyticsBucket) => (
              <div
                key={d._id}
                className="flex justify-between items-center py-2 border-b border-gray-100"
              >
                <span className="text-gray-600 capitalize">{d._id}</span>
                <span className="font-semibold">{d.count}</span>
              </div>
            ))}
            {(contentAnalytics?.byDifficulty ?? []).length === 0 && (
              <p className="text-gray-400 text-sm">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
